import { useQuery } from '@apollo/client'
import Alert from '@mui/material/Alert'
import Chip from '@mui/material/Chip'
import Grid from '@mui/material/Grid'
import Link from '@mui/material/Link'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import { useParams } from 'react-router-dom'
import { ProfilePanel } from 'recruiter/components/ProfilePanel'
import {
    GetCandidateBucketAudits,
    GetCandidateQuestionnaireAnswers,
    GetSchedulerDiagnostic,
} from 'recruiter/graphql/candidateBucketAudit.gql'
import { LoadingPanel } from 'shared/components/LoadingPanel'
import { getPxAsRem } from 'shared/utils'
import EmailAuditList from './EmailAuditList'

class UnexpectedFormat extends Error {}

type AnswerFormatter = (response: any) => string

const answerFormatters: Record<string, AnswerFormatter> = {
    country_hometown: (response) => {
        const result = [
            response.city,
            response.state?.choice,
            response.country?.choice,
        ]
            .filter(Boolean)
            .join(', ')
        if (!result) throw new UnexpectedFormat()
        return result
    },

    employment: (response) => {
        const who = [
            response.company?.choice,
            response.workTitle,
            response.companyOffice?.choice,
        ]
            .filter(Boolean)
            .join(' — ')
        if (!who) throw new UnexpectedFormat()
        const dates = [response.startDate, response.endDate]
            .filter(Boolean)
            .join(' – ')
        return dates ? `${who} (${dates})` : who
    },

    undergraduate_college: (response) => {
        if (!Array.isArray(response)) throw new UnexpectedFormat()
        const result = response
            .map((entry: any) => {
                const majors = entry.majors
                    ?.map((m: any) => m.choice)
                    .join(', ')
                return [
                    entry.college?.choice,
                    majors,
                    entry.gpa?.choice &&
                        `GPA: ${entry.gpa.choice}/${entry.gpaScale ?? '4.0'}`,
                    entry.graduationDate,
                ]
                    .filter(Boolean)
                    .join(' — ')
            })
            .join('\n')
        if (!result) throw new UnexpectedFormat()
        return result
    },

    graduate_degrees: (response) => {
        if (!Array.isArray(response)) throw new UnexpectedFormat()
        const result = response
            .map((entry: any) => {
                const major =
                    entry.major?.choice ??
                    entry.majors?.map((m: any) => m.choice).join(', ')
                return [
                    entry.college?.choice,
                    major,
                    entry.gpa?.choice &&
                        `GPA: ${entry.gpa.choice}/${entry.gpaScale ?? '4.0'}`,
                    entry.graduationDate,
                ]
                    .filter(Boolean)
                    .join(' — ')
            })
            .join('\n')
        if (!result) throw new UnexpectedFormat()
        return result
    },

    date_picker: (response) => {
        if (typeof response !== 'string') throw new UnexpectedFormat()
        return response
    },

    internship: (response) => {
        const parts = [response.company?.choice, response.companyOffice?.choice]
            .filter(Boolean)
            .join(', ')
        const industries = response.industries
            ?.map((i: any) => i.choice)
            .join(', ')
        const returnStatus = response.returnOffer
            ? 'Return offer'
            : response.returnOffer === false
              ? 'No return offer'
              : null
        const returnReason = response.returnReason
            ? `(${response.returnReason})`
            : null
        const result = [parts, industries, returnStatus, returnReason]
            .filter(Boolean)
            .join(' — ')
        if (!result) throw new UnexpectedFormat()
        return result
    },

    analysts: (response) => {
        if (!Array.isArray(response)) throw new UnexpectedFormat()
        const result = response
            .map((entry: any) => entry.analyst)
            .filter(Boolean)
            .join(', ')
        if (!result) throw new UnexpectedFormat()
        return result
    },

    reference: (response) => {
        if (!Array.isArray(response)) throw new UnexpectedFormat()
        return response
            .map((ref: any) => {
                const name = `${ref.firstName} ${ref.lastName}`
                const company = ref.company?.choice ?? ref.company?.value
                const title = ref.workTitle
                const detail = [title, company].filter(Boolean).join(' at ')
                return detail ? `${name} (${detail})` : name
            })
            .join('; ')
    },

    staffer: (response) => {
        if (!response.firstName) throw new UnexpectedFormat()
        return `${response.firstName} ${response.lastName}`
    },

    document_upload: (response) => {
        const uploaded: string[] = []
        if (response.resumeUploaded) uploaded.push('Resume')
        if (response.profilePictureUploaded) uploaded.push('Profile Picture')
        if (!uploaded.length) throw new UnexpectedFormat()
        return `Uploaded: ${uploaded.join(', ')}`
    },

    standardized_test_scores: (response) => {
        const labels: Record<string, string> = {
            satTotal: 'SAT Total',
            satMathScore: 'SAT Math',
            satVerbalScore: 'SAT Verbal',
            satWritingScore: 'SAT Writing',
            actScore: 'ACT',
            gmatScore: 'GMAT',
            greTotalScore: 'GRE Total',
            greVerbalScore: 'GRE Verbal',
            greQuantitativeScore: 'GRE Quantitative',
            greWritingScore: 'GRE Writing',
        }
        const scores = Object.entries(labels)
            .filter(([key]) => response[key] != null)
            .map(([key, label]) => `${label}: ${response[key]}`)
        return scores.join(', ') || '—'
    },

    dropdown_multiselect: (response) => {
        const outerKey = Object.keys(response)[0]
        if (!outerKey) return '—'
        const ranks = response[outerKey]
        if (typeof ranks !== 'object') return '—'
        const lines: string[] = []
        const sortedKeys = Object.keys(ranks).sort()
        sortedKeys.forEach((key, idx) => {
            const choices = ranks[key]
            if (!Array.isArray(choices)) return
            const names = choices
                .map((c: any) => c.customValue || c.choice)
                .filter(Boolean)
                .join(', ')
            if (names) lines.push(`${idx + 1}. ${names}`)
        })
        return lines.join('\n') || '—'
    },
}

function formatAnswerFallback(response: any): string {
    if (Array.isArray(response)) {
        const choices = response
            .map((item: any) => item.choice ?? item)
            .filter(Boolean)
            .join(', ')
        if (choices) return choices
    } else if (response.choice) {
        return response.choice
    } else if (typeof response === 'object') {
        const values = Object.values(response) as any[]
        if (values.length === 1 && values[0]?.choice) return values[0].choice
        const choices = values.filter((v) => v?.choice).map((v) => v.choice)
        if (choices.length) return choices.join(', ')
    }
    throw new UnexpectedFormat()
}

function formatAnswer(
    answerData: Record<string, any>,
    questionType: string
): string {
    const response = answerData?.response
    if (response == null) return JSON.stringify(answerData, null, 2)
    try {
        const formatter = answerFormatters[questionType]
        return formatter ? formatter(response) : formatAnswerFallback(response)
    } catch (e) {
        if (e instanceof UnexpectedFormat)
            return JSON.stringify(response, null, 2)
        throw e
    }
}

export default function Communications() {
    const { candidateId } = useParams()

    const { data: auditData, loading: auditLoading } = useQuery(
        GetCandidateBucketAudits,
        {
            variables: { candidateId: candidateId! },
            skip: !candidateId,
        }
    )

    const { data: answersData, loading: answersLoading } = useQuery(
        GetCandidateQuestionnaireAnswers,
        {
            variables: { candidateId: candidateId! },
            skip: !candidateId,
        }
    )

    const { data: diagnosticData, loading: diagnosticLoading } = useQuery(
        GetSchedulerDiagnostic,
        {
            variables: { candidateId: candidateId! },
            skip: !candidateId,
        }
    )

    if (auditLoading || answersLoading || diagnosticLoading)
        return <LoadingPanel />

    const audits = auditData?.candidateBucketAudits ?? []
    const answers = answersData?.candidateQuestionnaireAnswers ?? []

    const answersByPart = answers.reduce<Record<string, typeof answers>>(
        (acc, answer) => {
            const part = answer.intakePart
            if (!acc[part]) acc[part] = []
            acc[part].push(answer)
            return acc
        },
        {}
    )

    const diagnostic =
        diagnosticData?.candidate?.interviewBucket?.schedulerDiagnostic

    return (
        <Stack gap={getPxAsRem(24)}>
            {diagnostic && (
                <Alert
                    severity={diagnostic.visible ? 'success' : 'info'}
                    variant="outlined"
                >
                    {diagnostic.visible
                        ? `Candidate can see scheduler — ${diagnostic.reason}`
                        : `Candidate cannot see scheduler — ${diagnostic.reason}`}
                </Alert>
            )}

            <EmailAuditList />

            <ProfilePanel panelTitle="Bucket Assignment">
                {audits.length === 0 ? (
                    <Typography color="text.secondary">
                        No bucket audit records.
                    </Typography>
                ) : (
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Date</TableCell>
                                    <TableCell>Bucket</TableCell>
                                    <TableCell>Bucket Reason</TableCell>
                                    <TableCell>Calendly Link</TableCell>
                                    <TableCell>Link Reason</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {audits.map((audit) => (
                                    <TableRow key={audit.id}>
                                        <TableCell
                                            title={new Date(
                                                audit.createdAt
                                            ).toLocaleString()}
                                        >
                                            {new Date(
                                                audit.createdAt
                                            ).toLocaleDateString()}
                                        </TableCell>
                                        <TableCell>
                                            {audit.bucket ? (
                                                <Chip
                                                    label={audit.bucket}
                                                    size="small"
                                                />
                                            ) : (
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                >
                                                    —
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {audit.reason
                                                ?.split(';')
                                                .map((r, i) => (
                                                    <div key={i}>{r}</div>
                                                )) ?? '—'}
                                        </TableCell>
                                        <TableCell>
                                            {audit.resolvedCalendlyLink ? (
                                                <Link
                                                    href={
                                                        audit.resolvedCalendlyLink
                                                    }
                                                    target="_blank"
                                                    rel="noopener"
                                                >
                                                    {audit.resolvedCalendlyLink}
                                                </Link>
                                            ) : (
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                >
                                                    None
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {audit.linkResolutionReason ?? '—'}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </ProfilePanel>

            <ProfilePanel panelTitle="Questionnaire Answers">
                {answers.length === 0 ? (
                    <Typography color="text.secondary">
                        No questionnaire answers recorded.
                    </Typography>
                ) : (
                    <Grid container spacing={getPxAsRem(24)}>
                        {Object.entries(answersByPart).map(
                            ([partName, partAnswers]) => (
                                <Grid item xs={12} md={6} key={partName}>
                                    <Typography
                                        variant="subtitle1"
                                        fontWeight="bold"
                                        gutterBottom
                                    >
                                        {partName}
                                    </Typography>
                                    <TableContainer>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell width="40%">
                                                        Question
                                                    </TableCell>
                                                    <TableCell>
                                                        Answer
                                                    </TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {partAnswers.map((answer) => (
                                                    <TableRow key={answer.id}>
                                                        <TableCell>
                                                            {
                                                                answer.questionDisplayName
                                                            }
                                                        </TableCell>
                                                        <TableCell
                                                            style={{
                                                                whiteSpace:
                                                                    'pre-wrap',
                                                            }}
                                                        >
                                                            {formatAnswer(
                                                                answer.answerData,
                                                                answer.questionType
                                                            )}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Grid>
                            )
                        )}
                    </Grid>
                )}
            </ProfilePanel>
        </Stack>
    )
}
